export interface AdaptiveTemplateDefinition {
  id: string;
  text: string;
  family: string;
  difficulty: 1 | 2 | 3;
}

const SUBJECTS = [
  "The careful writer", "A patient learner", "The support specialist",
  "A focused developer", "The studio editor", "A calm researcher",
  "The project coordinator", "A thoughtful student", "The workshop lead",
  "A practiced musician", "The service designer", "A precise technician",
  "The community librarian", "A reliable teammate", "The field reporter",
  "A curious analyst", "The rehearsal director", "A steady typist",
  "The archive assistant", "A new team member",
] as const;

const PREDICATES = [
  "keeps each movement light while a clear sentence takes shape",
  "reviews the next phrase before increasing speed",
  "returns to a steady rhythm after correcting one mistake",
  "checks the difficult transition without tensing the hands",
  "reads a few words ahead and preserves accurate spacing",
  "builds dependable control through short deliberate sessions",
  "notices the recurring pattern and practices it calmly",
  "uses a modest pace to protect clean first attempts",
  "finishes the line with the same control used at the start",
  "lets accuracy establish the rhythm before adding pressure",
  "records the result honestly and chooses one useful next step",
  "repeats the awkward motion until it begins to feel ordinary",
  "keeps the shoulders relaxed during a longer paragraph",
  "treats punctuation with the same care as ordinary letters",
  "uses quiet corrections instead of chasing a perfect burst",
  "compares several attempts before judging whether skill improved",
  "pauses briefly when technique starts to feel forced",
  "practices the weak pattern while stronger movements stay available",
  "balances attention between the current word and the phrase ahead",
  "allows consistent timing to grow from accurate repetition",
  "chooses a readable exercise that matches the evidence",
  "keeps the next key visible in attention without staring at the hands",
  "separates a difficult reach from the pressure of the full task",
  "uses complete words so the target pattern remains meaningful",
  "ends the session before fatigue turns practice into guessing",
] as const;

const GUIDANCE = [
  "Smooth repetition makes the target easier to recognize in ordinary work.",
  "The goal is a reliable motion, not a single dramatic score.",
  "A clean first attempt provides stronger evidence than a rushed correction.",
  "Small accurate gains accumulate when the same habit returns tomorrow.",
  "Relaxed attention leaves enough room to notice the pattern and the message.",
  "Useful practice stays specific, measurable, and brief enough to repeat.",
  "When control remains high, pace can rise without changing the technique.",
  "A short review after the attempt turns the result into practical direction.",
  "The exercise remains readable so the pattern appears in a natural context.",
  "Consistent spacing and punctuation keep the passage honest and complete.",
] as const;

export const ADAPTIVE_TEMPLATE_CATALOG: readonly AdaptiveTemplateDefinition[] =
  SUBJECTS.flatMap((subject, subjectIndex) =>
    PREDICATES.map((predicate, predicateIndex) => ({
      id: `adaptive-template-${String(subjectIndex + 1).padStart(2, "0")}-${String(predicateIndex + 1).padStart(2, "0")}`,
      text: `${subject} ${predicate}. ${GUIDANCE[(subjectIndex * 7 + predicateIndex * 3) % GUIDANCE.length]}`,
      family: `practice-${String(predicateIndex + 1).padStart(2, "0")}`,
      difficulty: (predicateIndex % 5 === 0 ? 3 : predicateIndex % 2 === 0 ? 2 : 1) as 1 | 2 | 3,
    })),
  );

export interface AdaptiveTemplateValidationIssue {
  code: "count" | "duplicate-id" | "duplicate-text" | "unreadable" | "placeholder";
  templateId?: string;
  message: string;
}

export function validateAdaptiveTemplateCatalog(
  catalog: readonly AdaptiveTemplateDefinition[] = ADAPTIVE_TEMPLATE_CATALOG,
): AdaptiveTemplateValidationIssue[] {
  const issues: AdaptiveTemplateValidationIssue[] = [];
  const ids = new Set<string>();
  const texts = new Set<string>();
  if (catalog.length !== 500) {
    issues.push({ code: "count", message: `Expected 500 adaptive templates; found ${catalog.length}.` });
  }
  for (const template of catalog) {
    if (ids.has(template.id)) {
      issues.push({ code: "duplicate-id", templateId: template.id, message: `Duplicate adaptive template ID: ${template.id}` });
    }
    ids.add(template.id);
    const normalized = template.text.normalize("NFKC").toLocaleLowerCase("en-US").replace(/\s+/g, " ").trim();
    if (texts.has(normalized)) {
      issues.push({ code: "duplicate-text", templateId: template.id, message: `Duplicate adaptive template text: ${template.id}` });
    }
    texts.add(normalized);
    const words = normalized.match(/[a-z]+(?:['’-][a-z]+)?/g) ?? [];
    if (words.length < 18 || words.length > 45 || template.text.length < 100) {
      issues.push({ code: "unreadable", templateId: template.id, message: `Adaptive template is outside the readability envelope: ${template.id}` });
    }
    if (/\{\{?|\}\}?|\[[A-Z_]+\]|<target>/i.test(template.text)) {
      issues.push({ code: "placeholder", templateId: template.id, message: `Adaptive template leaked a placeholder: ${template.id}` });
    }
  }
  return issues;
}
